import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { SectionCard, StatusChip, EmptyState } from '../../../../components/ui';
import { brandHome } from '../../brandHomeTokens';
import { SEVERITY_TONES, severityToTone } from '../../severityTones';
import type { EmailIngestionRecord } from '../../useBrandHomeData';

interface RebateFilesCardProps {
  records: EmailIngestionRecord[];
  loading?: boolean;
}

const STATUS_LABEL: Record<EmailIngestionRecord['status'], string> = {
  success: 'Saved',
  partial: 'Partial',
  error: 'Error',
};

const STATUS_TONE: Record<EmailIngestionRecord['status'], keyof typeof SEVERITY_TONES> = {
  success: 'positive',
  partial: 'warning',
  error: 'blocking',
};

export const STAFF_UPLOAD_LABEL = 'Uploaded by Motzu on your behalf';

// Brand-facing view: never render an internal Motzu staff identity. The API
// already redacts these rows (uploadedByMotzu); the @motzulabs.com check is a
// defensive backstop in case an older/unredacted payload reaches the client.
export const getSenderLabel = (record: EmailIngestionRecord & { uploadedByMotzu?: boolean }): string => {
  if (record.uploadedByMotzu || /@motzulabs\.com\b/i.test(record.from || '')) {
    return STAFF_UPLOAD_LABEL;
  }
  return record.from;
};

// No vendor-facing detail page exists yet for a given ingestion record, so
// there is no real destination for a "View"/"Fix rows"/"Retry" action —
// showing one would be an inert, misleading link. Four columns, no action.
const GRID = 'minmax(64px,auto) minmax(0,1fr) minmax(56px,auto) minmax(72px,auto)';

export const RebateFilesCard: React.FC<RebateFilesCardProps> = ({ records, loading }) => {
  const last = records[0];

  return (
    <SectionCard
      dense
      title="Rebate files we received from you"
      actions={
        last && (
          <Typography sx={{ fontSize: '12.5px', color: brandHome.color.muted }}>
            Last received {new Date(last.receivedAt).toLocaleString()}
          </Typography>
        )
      }
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'grid' },
          gridTemplateColumns: { xs: '1fr', md: GRID },
          p: '9px 20px',
          borderBottom: `1px solid ${brandHome.color.hairline}`,
          bgcolor: brandHome.color.subtleSurface,
        }}
      >
        {['Received', 'File', 'Records', 'Status'].map((label, i) => (
          <Typography
            key={label + i}
            sx={{
              fontSize: '10.5px',
              fontWeight: 700,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color: brandHome.color.faint,
              textAlign: i === 2 ? 'right' : i === 3 ? 'center' : 'left',
            }}
          >
            {label}
          </Typography>
        ))}
      </Box>
      {loading ? (
        <Box sx={{ p: '11px 20px' }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="text" height={32} />
          ))}
        </Box>
      ) : records.length === 0 ? (
        <EmptyState size="compact" title="No files received yet." />
      ) : (
        records.map((record) => {
          const tone = SEVERITY_TONES[STATUS_TONE[record.status]];
          const recordsLabel =
            record.status === 'partial'
              ? `${record.recordsSaved} / ${record.recordsParsed}`
              : String(record.recordsSaved);
          return (
            <Box
              key={record.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0,1fr)', md: GRID },
                gap: { xs: '4px', md: 0 },
                alignItems: { xs: 'stretch', md: 'center' },
                p: '11px 20px',
                borderBottom: `1px solid ${brandHome.color.rowHairline}`,
              }}
            >
              <Typography sx={{ fontSize: '12.5px', color: brandHome.color.bodyAlt }}>
                {new Date(record.receivedAt).toLocaleDateString()}
              </Typography>
              <Box sx={{ minWidth: 0, pr: { xs: 0, md: '14px' } }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {record.subject || `${record.errors?.length ?? 0} attachments`}
                </Typography>
                <Typography sx={{ fontSize: '11.5px', color: brandHome.color.faint }}>{getSenderLabel(record)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Typography
                  sx={{
                    display: { xs: 'inline', md: 'none' },
                    fontSize: '10.5px',
                    fontWeight: 700,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    color: brandHome.color.faint,
                  }}
                >
                  Records
                </Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, textAlign: 'right', pr: { xs: 0, md: '14px' }, color: tone.ink }}>
                  {recordsLabel}
                </Typography>
              </Box>
              <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                <StatusChip label={STATUS_LABEL[record.status]} tone={severityToTone(STATUS_TONE[record.status])} />
              </Box>
            </Box>
          );
        })
      )}
    </SectionCard>
  );
};
